import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiCall } from '@/lib/api-client';

/**
 * Optional prefill payload for opening the chat in 'new conversation' mode
 * with a recipient and/or first message already filled in.  Used by, e.g.,
 * the agent QueuePage 'overdue' card to escalate to an uploader.
 */
export interface ChatPrefill {
  recipientRoles?: Array<'admin' | 'supervisor' | 'agent' | 'uploader' | 'navigator'>;
  recipientId?: string;
  subject?: string;
  message?: string;
  // When true, ChatPanel goes straight to the new-thread compose form on open.
  composeImmediately?: boolean;
  // F5-R6b: el prefill puede llegar con el caso ya vinculado (el dibujo:
  // «el prefill aterriza como un hilo con el caso ya vinculado»).
  relatedClientId?: string;
  relatedAssignmentId?: string;
}

interface ChatUiState {
  open: boolean;
  selectedThreadId: string | null;
  prefill: ChatPrefill | null;
  setOpen: (b: boolean) => void;
  selectThread: (id: string | null) => void;
  openWithPrefill: (p: ChatPrefill) => void;
  clearPrefill: () => void;
}
export const useChatUi = create<ChatUiState>((set) => ({
  open: false,
  selectedThreadId: null,
  prefill: null,
  setOpen: (b) => set({ open: b, prefill: b ? undefined : null } as Partial<ChatUiState>),
  selectThread: (id) => set({ selectedThreadId: id }),
  openWithPrefill: (p) => set({ open: true, prefill: p, selectedThreadId: null }),
  clearPrefill: () => set({ prefill: null }),
}));

export interface Recipient {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

export interface ThreadRow {
  id: string;
  subject: string;
  opened_by: string;
  related_assignment_id: string | null;
  related_client_id: string | null;
  last_message_at: string;
  created_at: string;
  closed_at: string | null;
  participants: string[];
  last_message_body: string | null;
  last_sender: string | null;
  unread: number;
}

export interface ChatMessage {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_role?: string;
  body: string;
  sent_at: string;
}

// ===== F5-R6b — Chat v2 «Mensajería del equipo» (dibujo del CEO) =====

/** Recibo por hilo: last_read_at por participante (chat_thread_reads). */
export interface ThreadRead {
  user_id: string;
  full_name: string;
  last_read_at: string;
}

export interface ThreadReaction {
  message_id: string;
  emoji: string;
  count: number;
  mine: boolean;
}

/** Incorporación posterior a la creación (línea de sistema del stream). */
export interface ThreadJoin {
  user_id: string;
  full_name: string;
  joined_at: string;
  added_by_user_id: string | null;
  added_by_name: string | null;
}

export interface ThreadMember {
  id: string;
  full_name: string;
  role: string;
  active: boolean;
}

export interface ThreadMessagesPayload {
  threadId: string;
  messages: ChatMessage[];
  reads: ThreadRead[];
  reactions: ThreadReaction[];
  joins: ThreadJoin[];
  members: ThreadMember[];
}

export type PresenceState = 'free' | 'call' | 'off';

export function usePresenceMap(enabled: boolean) {
  return useQuery({
    queryKey: ['chat', 'presence'],
    queryFn: () => apiCall<Record<string, PresenceState>>('/chat/presence'),
    enabled,
    refetchInterval: 20_000,
    staleTime: 10_000,
  });
}

export function useSetPresence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (state: PresenceState) =>
      apiCall<{ ok: true }>('/chat/presence', { method: 'POST', body: { state } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat', 'presence'] });
    },
  });
}

export function useReact(threadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; emoji: string }) =>
      apiCall<{ ok: true }>(`/chat/messages/${input.messageId}/reactions`, {
        method: 'POST',
        body: { emoji: input.emoji },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat', 'thread', threadId, 'messages'] });
    },
  });
}

export function useAddParticipant(threadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiCall<{ ok: true }>(`/chat/threads/${threadId}/participants`, {
        method: 'POST',
        body: { userId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat', 'thread', threadId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['chat', 'threads'] });
    },
  });
}

export function useLinkCase(threadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { clientId: string; assignmentId?: string; title?: string }) =>
      apiCall<{ ok: true }>(`/chat/threads/${threadId}/link-case`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat', 'thread', threadId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['chat', 'threads'] });
    },
  });
}

/**
 * Silenciar conversación — por dispositivo (localStorage), a propósito:
 * no existe mute server-side (NECESITA-SERVER). Silenciar apaga el énfasis
 * visual y el contador de la fila; el badge global del shell sigue contando
 * (deviation declarada — restarlo exigiría que el botón del shell trajera la
 * lista entera de hilos en cada poll).
 */
interface ChatMuteState {
  muted: Record<string, true>;
  toggleMute: (threadId: string) => void;
}
export const useChatMutes = create<ChatMuteState>()(
  persist(
    (set) => ({
      muted: {},
      toggleMute: (threadId) =>
        set((s) => {
          const next = { ...s.muted };
          if (next[threadId]) delete next[threadId];
          else next[threadId] = true;
          return { muted: next };
        }),
    }),
    { name: 'pathway.chat.muted', storage: createJSONStorage(() => localStorage) },
  ),
);

export function useChatThreads() {
  return useQuery({
    queryKey: ['chat', 'threads'],
    queryFn: () => apiCall<ThreadRow[]>('/chat/threads'),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
}

export function useChatMessages(threadId: string | null) {
  return useQuery({
    // F5-R6b: el payload ganó reads/reactions/joins/members (aditivo server).
    queryKey: ['chat', 'thread', threadId, 'messages'],
    queryFn: () => apiCall<ThreadMessagesPayload>(`/chat/threads/${threadId}/messages`),
    enabled: !!threadId,
    refetchInterval: 15_000,
  });
}

export function useChatRecipients() {
  return useQuery({
    queryKey: ['chat', 'recipients'],
    queryFn: () => apiCall<Recipient[]>('/chat/recipients'),
    staleTime: 5 * 60_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['chat', 'unread'],
    queryFn: () => apiCall<{ count: number }>('/chat/unread-count'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export function useSendMessage(threadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => apiCall<{ id: string }>(`/chat/threads/${threadId}/messages`, {
      method: 'POST',
      body: { body },
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat', 'thread', threadId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['chat', 'threads'] });
      void qc.invalidateQueries({ queryKey: ['chat', 'unread'] });
    },
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subject: string;
      participants: string[];
      firstMessage?: string;
      relatedAssignmentId?: string;
      // F5-R6b: el server siempre lo aceptó (CreateThreadDto) — la UI nunca lo mandaba.
      relatedClientId?: string;
    }) => apiCall<{ id: string }>('/chat/threads', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat', 'threads'] });
    },
  });
}
